import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useUpgradeDialog from '../../../hooks/useUpgradeDialog';
import {
  getTutoringInterest,
  registerTutoringInterest,
} from '../../../service';
import { track as trackProductEvent } from '../../../utils/productEvents';
import {
  CONSENT_TEXT,
  ERRORS,
  FIELD_ORDER,
  applyPrefill,
  backLabel,
  failureMessage,
  FORMATS,
  GROUP_SIZES,
  LIMITS,
  OUTSIDE_AUSTRALIA,
  PRIVACY_URL,
  START_TIMINGS,
  STATES,
  SUBJECTS,
  YEARS,
  eventSource,
  firstName,
  initialValues,
  interestPayload,
  needsSuburb,
  summaryChips,
  toggleSubject,
  validate,
} from './tutoringForm';
import * as S from './style';

const Tick = ({ size = 16, stroke = 'currentColor', width = 2.5 }) => (
  <svg
    aria-hidden="true"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth={width}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const icon = (paths) => (
  <svg
    aria-hidden="true"
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2E2277"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {paths}
  </svg>
);

const ICONS = {
  ONLINE: icon(
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </>
  ),
  IN_PERSON: icon(
    <>
      <path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </>
  ),
  EITHER: icon(<path d="M7 7h11l-3-3M17 17H6l3 3" />),
  ONE_TO_ONE: icon(
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c1-3.5 3.8-5.5 7-5.5s6 2 7 5.5" />
    </>
  ),
  SMALL_GROUP: icon(
    <>
      <circle cx="9" cy="8.5" r="3" />
      <circle cx="17" cy="9.5" r="2.5" />
      <path d="M3 19.5c.8-3 3.2-4.8 6-4.8s5.2 1.8 6 4.8M15.5 14.8c2.3.1 4.3 1.6 5 4.2" />
    </>
  ),
  NOT_SURE: icon(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.6v.6M12 17h.01" />
    </>
  ),
};

const OptionCards = ({ options, value, onPick, field, describedBy }) => (
  <S.Options>
    {options.map((option, index) => (
      <S.OptionCard
        key={option.value}
        type="button"
        aria-pressed={value === option.value}
        aria-describedby={describedBy}
        data-field={index === 0 ? field : undefined}
        onClick={() => onPick(option.value)}
      >
        {ICONS[option.value]}
        <S.OptionText>
          <S.OptionTitle>{option.label}</S.OptionTitle>
          <S.OptionHint>{option.hint}</S.OptionHint>
        </S.OptionText>
        {value === option.value && (
          <S.OptionCheck>
            <Tick size={14} stroke="#FFFFFF" width={3} />
          </S.OptionCheck>
        )}
      </S.OptionCard>
    ))}
  </S.Options>
);

/**
 * The tutoring expression-of-interest form (JED-1486), opened from the Tutoring card in the student
 * plans popup. A dialog on desktop, a full-screen sheet on phones. Opening it reads the account's name
 * and email for the prefill (the profile has no email). `source` is the backend's
 * CREDIT_WALL / HEADER / ONBOARDING / PHONE; `trigger` the popup's product-event trigger; `fromDraft`
 * says the plans were opened over a draft (the phone editor), for the "Back to my draft" way out.
 * `onRegistered` runs once the backend has stored the lead; `onBack` returns to the plans popup and
 * `onDone` closes everything.
 */
const TutoringInterest = ({
  isOpen,
  profile = null,
  source = 'CREDIT_WALL',
  trigger = '',
  fromDraft = false,
  onBack,
  onDone,
  onRegistered,
}) => {
  const [values, setValues] = useState(() => initialValues(profile));
  const [tried, setTried] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // null while filling in; 'new' once stored; 'earlier' when the student had already registered.
  const [sent, setSent] = useState(null);
  // The field a 400 named, shown inline until they change something.
  const [refusedField, setRefusedField] = useState(null);
  const submittingRef = useRef(false);
  const openCountRef = useRef(0);
  const id = useId();
  const titleId = `${id}-title`;
  const closeIfIdle = (close) => () => {
    if (!submittingRef.current) close?.();
  };
  const dialogRef = useUpgradeDialog(
    isOpen,
    closeIfIdle(sent ? onDone : onBack)
  );

  useEffect(() => {
    if (!isOpen) return;
    openCountRef.current += 1;
    const opening = openCountRef.current;
    setValues(initialValues(profile));
    setTried(false);
    setSubmitError('');
    setSent(null);
    setRefusedField(null);
    trackProductEvent('tutoring_interest_opened', {
      source: eventSource(source),
    });
    // Start on the first field, not the close button.
    window.requestAnimationFrame(() =>
      dialogRef.current?.querySelector('[data-field="name"]')?.focus()
    );
    // The account's email (and name) fill only what is still empty; a failed read leaves them to type.
    Promise.resolve()
      .then(() => getTutoringInterest({ prefill: true }))
      .then((status) => {
        if (openCountRef.current === opening && status?.prefill) {
          setValues((current) => applyPrefill(current, status.prefill));
        }
      })
      .catch(() => {});
  }, [isOpen]);

  if (!isOpen) return null;

  const errors = {
    ...(tried ? validate(values) : {}),
    ...(refusedField && ERRORS[refusedField]
      ? { [refusedField]: ERRORS[refusedField] }
      : {}),
  };
  const change = (update) => {
    setRefusedField(null);
    setValues(update);
  };
  const set = (key, limit) => (event) => {
    const next = event.target.value;
    change((current) => ({
      ...current,
      [key]: limit ? next.slice(0, limit) : next,
    }));
  };
  const pick = (key) => (value) =>
    change((current) => ({ ...current, [key]: value }));
  const toggle = (key) => () =>
    change((current) => ({ ...current, [key]: !current[key] }));

  const focusFirstError = (found) => {
    const first = FIELD_ORDER.find((key) => found[key]);
    window.requestAnimationFrame(() =>
      dialogRef.current?.querySelector(`[data-field="${first}"]`)?.focus()
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    setTried(true);
    const found = validate(values);
    if (Object.keys(found).length) {
      focusFirstError(found);
      return;
    }
    const opening = openCountRef.current;
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError('');
    setRefusedField(null);
    let result;
    try {
      result = await registerTutoringInterest(
        interestPayload(values, { source, trigger })
      );
    } catch (error) {
      // Maintenance mode: service.js shows its own notice; the form just stops sending.
      result = undefined;
    } finally {
      submittingRef.current = false;
      if (openCountRef.current === opening) setSubmitting(false);
    }
    if (openCountRef.current !== opening) return;
    // Only an explicit { registered: true } is a stored lead.
    if (result?.registered !== true) {
      // undefined = the session expired (service.js is sending them to sign in) or maintenance.
      if (result === undefined) return;
      setSubmitError(failureMessage(result));
      if (result.error === 'invalid' && ERRORS[result.field]) {
        setRefusedField(result.field);
        focusFirstError({ [result.field]: true });
      }
      return;
    }
    if (!result.alreadyRegistered)
      trackProductEvent('tutoring_interest_submitted', {
        source: eventSource(source),
      });
    onRegistered?.(result);
    setSent(result.alreadyRegistered ? 'earlier' : 'new');
    // The submit button is gone; put focus on the thank-you's way out.
    window.requestAnimationFrame(() =>
      dialogRef.current?.querySelector('[data-sent-action]')?.focus()
    );
  };

  const errorFor = (key) =>
    errors[key] && (
      <S.FieldError role="alert" id={`${id}-${key}-error`}>
        {ERRORS[key]}
      </S.FieldError>
    );
  const describedBy = (key) => (errors[key] ? `${id}-${key}-error` : undefined);
  const wayOut = backLabel({ trigger, fromDraft });
  const yearOptions =
    values.year && !YEARS.includes(values.year)
      ? [values.year, ...YEARS]
      : YEARS;

  return createPortal(
    <S.Overlay>
      <S.Dialog
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <S.TopBar>
          <S.Eyebrow as="span">Tutoring</S.Eyebrow>
          <S.CloseButton
            type="button"
            aria-label="Close"
            onClick={closeIfIdle(sent ? onDone : onBack)}
            disabled={submitting}
          >
            ×
          </S.CloseButton>
        </S.TopBar>
        {sent === 'earlier' ? (
          // Nothing they just typed was stored, so none of it is shown back.
          <S.Sent>
            <S.SuccessIcon>
              <Tick size={32} stroke="#FFFFFF" />
            </S.SuccessIcon>
            <S.SentTitle id={titleId}>You’d already registered.</S.SentTitle>
            <S.SentText>
              We have the details you sent before, so there’s nothing more to
              do. You can keep using JeddAI as normal.
            </S.SentText>
            <S.BackButton
              type="button"
              data-sent-action
              onClick={() => onDone?.()}
            >
              {wayOut}
            </S.BackButton>
            <S.SmallNote>
              Need to change something? Reply to our email.
            </S.SmallNote>
          </S.Sent>
        ) : sent ? (
          <S.Sent>
            <S.SuccessIcon>
              <Tick size={32} stroke="#FFFFFF" />
            </S.SuccessIcon>
            <S.SentTitle id={titleId}>
              Thanks, {firstName(values.name)}. You’re on the list.
            </S.SentTitle>
            <S.SentText>
              We’ll email {values.email.trim()} to talk through tutoring
              options. You can keep using JeddAI as normal.
            </S.SentText>
            <S.SummaryChips>
              {summaryChips(values).map((chip) => (
                <S.SummaryChip key={chip}>{chip}</S.SummaryChip>
              ))}
            </S.SummaryChips>
            <S.BackButton
              type="button"
              data-sent-action
              onClick={() => onDone?.()}
            >
              {wayOut}
            </S.BackButton>
            <S.SmallNote>
              Need to change something? Reply to our email.
            </S.SmallNote>
          </S.Sent>
        ) : (
          <>
            <S.Hero>
              <S.Eyebrow>Tutoring</S.Eyebrow>
              <S.Title id={titleId}>
                Find a tutor who fits how you learn
              </S.Title>
              <S.Lead>
                Tell us what you need and we’ll be in touch. It’s free to
                register and there’s no commitment.
              </S.Lead>
            </S.Hero>

            <S.Form noValidate onSubmit={handleSubmit}>
              <S.Section>
                <S.SectionTitle>About you</S.SectionTitle>
                <S.FieldGrid>
                  <S.Field>
                    <S.Label htmlFor={`${id}-name`}>Your name</S.Label>
                    <S.Input
                      id={`${id}-name`}
                      data-field="name"
                      type="text"
                      autoComplete="name"
                      value={values.name}
                      onChange={set('name', LIMITS.name)}
                      $invalid={Boolean(errors.name)}
                      aria-invalid={Boolean(errors.name)}
                      aria-describedby={describedBy('name')}
                    />
                    {errorFor('name')}
                  </S.Field>
                  <S.Field>
                    <S.Label htmlFor={`${id}-email`}>Email</S.Label>
                    <S.Input
                      id={`${id}-email`}
                      data-field="email"
                      type="email"
                      autoComplete="email"
                      value={values.email}
                      onChange={set('email', LIMITS.email)}
                      $invalid={Boolean(errors.email)}
                      aria-invalid={Boolean(errors.email)}
                      aria-describedby={describedBy('email')}
                    />
                    {errorFor('email')}
                  </S.Field>
                  <S.Field>
                    <S.Label htmlFor={`${id}-phone`}>Mobile</S.Label>
                    <S.Input
                      id={`${id}-phone`}
                      data-field="phone"
                      type="tel"
                      autoComplete="tel"
                      placeholder="04xx xxx xxx"
                      value={values.phone}
                      onChange={set('phone', LIMITS.phone)}
                      $invalid={Boolean(errors.phone)}
                      aria-invalid={Boolean(errors.phone)}
                      aria-describedby={describedBy('phone')}
                    />
                    {errorFor('phone')}
                  </S.Field>
                  <S.Pair>
                    <S.Field>
                      <S.Label htmlFor={`${id}-year`}>Year</S.Label>
                      <S.Select
                        id={`${id}-year`}
                        value={values.year}
                        onChange={set('year')}
                      >
                        <option value="">Choose</option>
                        {yearOptions.map((year) => (
                          <option key={year}>{year}</option>
                        ))}
                      </S.Select>
                    </S.Field>
                    <S.Field>
                      <S.Label htmlFor={`${id}-state`}>State</S.Label>
                      <S.Select
                        id={`${id}-state`}
                        value={values.state}
                        onChange={set('state')}
                      >
                        <option value="">Choose</option>
                        {[...STATES, OUTSIDE_AUSTRALIA].map((state) => (
                          <option key={state}>{state}</option>
                        ))}
                      </S.Select>
                    </S.Field>
                  </S.Pair>
                </S.FieldGrid>
                <S.CheckRow>
                  <input
                    type="checkbox"
                    checked={values.addParent}
                    onChange={toggle('addParent')}
                  />
                  Also contact my parent or guardian
                </S.CheckRow>
                {values.addParent && (
                  <S.ParentBox>
                    <S.Field>
                      <S.Label htmlFor={`${id}-parent-name`}>
                        Parent or guardian name
                      </S.Label>
                      <S.Input
                        id={`${id}-parent-name`}
                        type="text"
                        value={values.parentName}
                        onChange={set('parentName', LIMITS.parentName)}
                      />
                    </S.Field>
                    <S.Field>
                      <S.Label htmlFor={`${id}-parent-contact`}>
                        Their email or phone
                      </S.Label>
                      <S.Input
                        id={`${id}-parent-contact`}
                        type="text"
                        value={values.parentContact}
                        onChange={set('parentContact', LIMITS.parentContact)}
                      />
                    </S.Field>
                  </S.ParentBox>
                )}
              </S.Section>

              <S.Section $gap={24}>
                <S.SectionTitle>What you’d like help with</S.SectionTitle>
                <S.Fieldset>
                  <S.Legend>
                    Subjects <S.Optional>· pick any</S.Optional>
                  </S.Legend>
                  <S.Chips>
                    {SUBJECTS.map((subject) => {
                      const on = values.subjects.includes(subject);
                      return (
                        <S.Chip
                          key={subject}
                          type="button"
                          aria-pressed={on}
                          onClick={() =>
                            change((current) => ({
                              ...current,
                              subjects: toggleSubject(
                                current.subjects,
                                subject
                              ),
                            }))
                          }
                        >
                          {on && <Tick />}
                          {subject}
                        </S.Chip>
                      );
                    })}
                  </S.Chips>
                </S.Fieldset>

                <S.Fieldset
                  aria-describedby={describedBy('format')}
                  aria-invalid={Boolean(errors.format)}
                >
                  <S.Legend>How would you like to meet?</S.Legend>
                  <OptionCards
                    options={FORMATS}
                    value={values.format}
                    onPick={pick('format')}
                    field="format"
                    describedBy={describedBy('format')}
                  />
                  {needsSuburb(values.format) && (
                    <S.Field style={{ maxWidth: 320, marginTop: 6 }}>
                      <S.Label htmlFor={`${id}-suburb`}>
                        Suburb or postcode
                      </S.Label>
                      <S.Input
                        id={`${id}-suburb`}
                        type="text"
                        placeholder="e.g. Parramatta 2150"
                        value={values.suburb}
                        onChange={set('suburb', LIMITS.suburb)}
                      />
                      <S.Hint>So we can look for a tutor near you.</S.Hint>
                    </S.Field>
                  )}
                  {errorFor('format')}
                </S.Fieldset>

                <S.Fieldset
                  aria-describedby={describedBy('groupSize')}
                  aria-invalid={Boolean(errors.groupSize)}
                >
                  <S.Legend>1:1 or group?</S.Legend>
                  <OptionCards
                    options={GROUP_SIZES}
                    value={values.groupSize}
                    onPick={pick('groupSize')}
                    field="groupSize"
                    describedBy={describedBy('groupSize')}
                  />
                  {errorFor('groupSize')}
                </S.Fieldset>

                <S.Fieldset>
                  <S.Legend>
                    When would you like to start?{' '}
                    <S.Optional>(optional)</S.Optional>
                  </S.Legend>
                  <S.Chips>
                    {START_TIMINGS.map((timing) => {
                      const on = values.startTiming === timing.value;
                      return (
                        <S.Chip
                          key={timing.value}
                          type="button"
                          aria-pressed={on}
                          onClick={() =>
                            pick('startTiming')(on ? null : timing.value)
                          }
                        >
                          {timing.label}
                        </S.Chip>
                      );
                    })}
                  </S.Chips>
                </S.Fieldset>

                <S.Field>
                  <S.Label htmlFor={`${id}-notes`}>
                    Anything we should know? <S.Optional>(optional)</S.Optional>
                  </S.Label>
                  <S.Textarea
                    id={`${id}-notes`}
                    rows={3}
                    placeholder="e.g. I want help planning essays under time pressure"
                    value={values.notes}
                    onChange={set('notes', LIMITS.notes)}
                  />
                </S.Field>
              </S.Section>

              <S.Section>
                <S.CheckRow $top>
                  <input
                    type="checkbox"
                    data-field="consent"
                    checked={values.consent}
                    onChange={toggle('consent')}
                    aria-invalid={Boolean(errors.consent)}
                    aria-describedby={describedBy('consent')}
                  />
                  <span>
                    {CONSENT_TEXT}{' '}
                    <a
                      href={PRIVACY_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      How we use your details
                    </a>
                  </span>
                </S.CheckRow>
                {errorFor('consent')}
              </S.Section>
              <S.Footer>
                {submitError && (
                  <S.FieldError role="alert">{submitError}</S.FieldError>
                )}
                <S.Actions>
                  <S.CancelButton
                    type="button"
                    onClick={closeIfIdle(onBack)}
                    disabled={submitting}
                  >
                    Cancel
                  </S.CancelButton>
                  <S.SubmitButton type="submit" disabled={submitting}>
                    {submitting ? 'Registering…' : 'Register interest'}
                  </S.SubmitButton>
                </S.Actions>
                <S.Note>
                  <span className="desktop">
                    No payment needed. Registering doesn’t change your JeddAI
                    plan.
                  </span>
                  <span className="phone">
                    No payment needed. Your JeddAI plan doesn’t change.
                  </span>
                </S.Note>
              </S.Footer>
            </S.Form>
          </>
        )}
      </S.Dialog>
    </S.Overlay>,
    document.body
  );
};

export default TutoringInterest;
